import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { captureException } from 'shared/sentry';
import { listenForMessages, handleIncomingFcmPayload, debugFetchFcmToken } from '../../utils/firebaseUtils';
import { resolveMessaging } from '../../config/firebase';
import { AUTH_TOKEN_KEYS } from '../../constants/common';
import userService from '../../services/userService';
import { fetchNotificationCount } from '../../actions/notificationAction';
import {
  FCM_SYNC_REASONS,
  PUSH_CONFIG,
  PUSH_EVENTS,
  PUSH_SENTRY_STEPS,
  PUSH_SENTRY_TAGS,
} from './pushConstants';
import { NOTIFICATION_FEATURE_FLAGS } from '../notificationFeatureFlags';
import { canSyncPushNotifications } from './pushEligibility';
import { isLoginWithSupportPassword, isUserLoggedIn } from '../../utils/function';
import {
  canUseWebPush,
  flushPushPermissionPoll,
  subscribePushPermissionChanges,
} from './pushPermission';
import {
  recordFcmTabHidden,
  recordFcmTabVisibleAndShouldSync,
  requestFcmSync,
  resetFcmSyncSession,
  scheduleFcmSyncWithRetry,
} from './fcmSyncCoordinator';
import { waitForIndexedDBReady } from './firebaseIdbReady';

const isAccessTokenSetEvent = (event) =>
  event?.detail?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN &&
  event?.detail?.action === 'set';

const isAccessTokenClearedEvent = (event) =>
  event?.detail?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN &&
  event?.detail?.action === 'remove';

const isAccessTokenStorageEvent = (event) =>
  event?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && Boolean(event?.newValue);

const FOREGROUND_LISTENER_RETRY_DELAYS_MS = [0, 750, 2000, 4000, 8000];

export const usePushNotifications = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();
  const fcmEnabled = enabled && NOTIFICATION_FEATURE_FLAGS.fcm && canUseWebPush();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!fcmEnabled) {
      return undefined;
    }

    let isMounted = true;
    let isListening = false;
    let stopListeningMessages = () => {};
    let foregroundRetryTimeoutId = null;
    let countRefreshTimeoutId = null;

    const syncOptions = {
      isUserEligible: canSyncPushNotifications,
      updateFcmToken: userService.updateFcmToken,
    };

    const stopForegroundListening = () => {
      if (!isListening) {
        return;
      }
      try {
        stopListeningMessages();
      } catch (_) {
        // ignore cleanup failures
      }
      stopListeningMessages = () => {};
      isListening = false;
    };

    const scheduleNotificationCountRefresh = () => {
      if (countRefreshTimeoutId != null) {
        window.clearTimeout(countRefreshTimeoutId);
      }

      countRefreshTimeoutId = window.setTimeout(() => {
        countRefreshTimeoutId = null;
        void dispatch(fetchNotificationCount());
      }, PUSH_CONFIG.COUNT_REFRESH_DEBOUNCE_MS);
    };

    const ensureForegroundListening = async () => {
      if (!isMounted || !isUserLoggedIn() || isLoginWithSupportPassword()) {
        stopForegroundListening();
        return;
      }

      if (isListening || typeof listenForMessages !== 'function') {
        return;
      }

      try {
        await waitForIndexedDBReady({ timeoutMs: 5000, pollMs: 150 });
        resolveMessaging();

        const unsubscribe = listenForMessages(scheduleNotificationCountRefresh);
        if (!unsubscribe) {
          return;
        }

        stopListeningMessages = unsubscribe;
        isListening = true;
      } catch (error) {
        if (error?.code === 'messaging/unsupported-browser') {
          return;
        }
        captureException(error, {
          tags: PUSH_SENTRY_TAGS,
          extra: { step: PUSH_SENTRY_STEPS.FOREGROUND_LISTENER },
        });
      }
    };

    const scheduleForegroundListenerRetry = () => {
      if (!isMounted || isListening) {
        return;
      }

      let attempt = 0;
      const tryAttach = async () => {
        if (!isMounted || isListening) {
          return;
        }

        await ensureForegroundListening();

        if (isListening || !isMounted) {
          return;
        }

        attempt += 1;
        if (attempt >= FOREGROUND_LISTENER_RETRY_DELAYS_MS.length) {
          return;
        }

        foregroundRetryTimeoutId = window.setTimeout(
          tryAttach,
          FOREGROUND_LISTENER_RETRY_DELAYS_MS[attempt]
        );
      };

      void tryAttach();
    };

    const requestSync = (reason) => {
      if (!canUseWebPush()) {
        ensureForegroundListening();
        return;
      }
      requestFcmSync(syncOptions, { reason, onSettled: ensureForegroundListening });
    };

    const requestAuthSync = () => {
      if (!canUseWebPush()) {
        ensureForegroundListening();
        return;
      }
      scheduleFcmSyncWithRetry(syncOptions, {
        reason: FCM_SYNC_REASONS.AUTH,
        onSettled: ensureForegroundListening,
      });
    };

    const onServiceWorkerMessage = (event) => {
      if (event?.data?.type !== 'jh-fcm-push' || !event?.data?.payload) {
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('pushNotification >> serviceWorkerMessage', event.data.payload);
      }

      try {
        handleIncomingFcmPayload(event.data.payload);
        scheduleNotificationCountRefresh();
      } catch (error) {
        captureException(error, {
          tags: PUSH_SENTRY_TAGS,
          extra: { step: 'service-worker-push' },
        });
      }
    };

    const onSameTabAuthChange = (event) => {
      if (isAccessTokenClearedEvent(event)) {
        resetFcmSyncSession();
        stopForegroundListening();
        return;
      }
      if (isAccessTokenSetEvent(event)) {
        requestAuthSync();
        ensureForegroundListening();
      }
    };

    const onStorage = (event) => {
      if (event?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && !event.newValue) {
        resetFcmSyncSession();
        stopForegroundListening();
        return;
      }
      if (isAccessTokenStorageEvent(event)) {
        requestAuthSync();
        ensureForegroundListening();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        recordFcmTabHidden();
        return;
      }

      flushPushPermissionPoll();
      if (recordFcmTabVisibleAndShouldSync()) {
        requestSync(FCM_SYNC_REASONS.RESUME);
      } else {
        ensureForegroundListening();
      }
    };

    const onCookieConsentChanged = () => {
      scheduleFcmSyncWithRetry(syncOptions, {
        reason: FCM_SYNC_REASONS.CONSENT,
        onSettled: ensureForegroundListening,
      });
    };

    if (isUserLoggedIn()) {
      scheduleFcmSyncWithRetry(syncOptions, {
        reason: FCM_SYNC_REASONS.BOOT,
        onSettled: ensureForegroundListening,
      });
    }
    scheduleForegroundListenerRetry();

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
    }

    const removePushPermissionSubscription = subscribePushPermissionChanges(() => {
      scheduleFcmSyncWithRetry(syncOptions, {
        reason: FCM_SYNC_REASONS.PERMISSION,
        onSettled: ensureForegroundListening,
      });
    });

    window.addEventListener('localStorageChange', onSameTabAuthChange);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(PUSH_EVENTS.CONSENT_CHANGED, onCookieConsentChanged);

    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      window.__jhDebugFcm = {
        sync: () =>
          scheduleFcmSyncWithRetry(syncOptions, { reason: FCM_SYNC_REASONS.USER_GESTURE }),
        getToken: () => debugFetchFcmToken(),
        state: () => ({
          fcmEnabled,
          loggedIn: isUserLoggedIn(),
          permission: typeof Notification !== 'undefined' ? Notification.permission : 'n/a',
          fcmToken: localStorage.getItem('fcmToken'),
        }),
      };
    }

    return () => {
      isMounted = false;
      if (foregroundRetryTimeoutId != null) {
        window.clearTimeout(foregroundRetryTimeoutId);
      }
      if (countRefreshTimeoutId != null) {
        window.clearTimeout(countRefreshTimeoutId);
      }
      stopForegroundListening();
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
      }
      window.removeEventListener('localStorageChange', onSameTabAuthChange);
      window.removeEventListener('storage', onStorage);
      removePushPermissionSubscription();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(PUSH_EVENTS.CONSENT_CHANGED, onCookieConsentChanged);
    };
  }, [dispatch, fcmEnabled]);
};
